import { isPrimaryRelationshipProvenance } from '@entangled-vega/shared';

import type { GraphPerson, RelationshipLens } from '../data/graph-types.js';
import {
  bestDocumentedPath,
  otherEndpoint,
  type GraphIndex,
  type GraphPath,
  type RelationshipConnection,
} from './graph.js';

const MAX_TEMPTATIONS = 3;
const MIN_GENERATIONAL_GAP_YEARS = 25;
const DEFAULT_ALTERNATIVE_SEARCH_BUDGET = 24;
const MAX_ALTERNATIVE_SEARCH_DEPTH = 3;

export type NextTemptationKind =
  'recognizable-direct' | 'two-hop-bridge' | 'unvisited-next-hop' | 'global-discovery';

export type NextTemptationReason =
  | {
      kind: 'documented-direct';
      relationshipId: string;
      relationshipLabel: string;
      lens: RelationshipLens;
    }
  | {
      kind: 'different-domain';
      fromDomain: string;
      toDomain: string;
    }
  | {
      kind: 'birth-year-gap';
      fromBirthYear: number;
      toBirthYear: number;
      yearsApart: number;
    }
  | {
      kind: 'global-fallback';
    };

export type NextTemptation = {
  kind: NextTemptationKind;
  targetId: string;
  targetName: string;
  /** Null means general discovery; it never implies a graph connection. */
  path: GraphPath | null;
  reason: NextTemptationReason;
};

export type NextTemptationOptions = {
  index: GraphIndex;
  personId: string;
  visitedPersonIds?: ReadonlySet<string> | readonly string[];
  excludedPersonIds?: ReadonlySet<string> | readonly string[];
  maximum?: number;
};

type RankedPerson = {
  person: GraphPerson;
  degree: number;
  visited: boolean;
};

type TwoHopCandidate = RankedPerson & {
  intermediaryId: string;
  intermediaryRecognition: number;
  reason: Extract<NextTemptationReason, { kind: 'different-domain' | 'birth-year-gap' }>;
};

function toSet(values: ReadonlySet<string> | readonly string[] | undefined): Set<string> {
  return new Set(values ?? []);
}

function compareRankedPeople(left: RankedPerson, right: RankedPerson): number {
  return (
    Number(left.visited) - Number(right.visited) ||
    (right.person.recognitionScore ?? 0) - (left.person.recognitionScore ?? 0) ||
    right.degree - left.degree ||
    left.person.name.localeCompare(right.person.name) ||
    left.person.id.localeCompare(right.person.id)
  );
}

function primaryConnection(
  connections: readonly RelationshipConnection[],
): RelationshipConnection | null {
  return (
    connections.find((connection) =>
      isPrimaryRelationshipProvenance(connection.representative.status),
    ) ?? null
  );
}

function eligibleDirectPeople(options: {
  index: GraphIndex;
  personId: string;
  visited: ReadonlySet<string>;
  excluded: ReadonlySet<string>;
}): RankedPerson[] {
  return (options.index.counterpartsByPerson.get(options.personId) ?? [])
    .filter(
      (counterpart) =>
        !options.excluded.has(counterpart.personId) &&
        primaryConnection(counterpart.connections) !== null,
    )
    .map((counterpart) => options.index.peopleById.get(counterpart.personId))
    .filter(
      (person): person is GraphPerson => person !== undefined && person.inclusionRole !== 'context',
    )
    .map((person) => ({
      person,
      degree: options.index.neighborsByPerson.get(person.id)?.length ?? 0,
      visited: options.visited.has(person.id),
    }))
    .sort(compareRankedPeople);
}

function documentedPathWithLength(
  index: GraphIndex,
  from: string,
  to: string,
  stepCount: 1 | 2,
): GraphPath | null {
  const path = bestDocumentedPath(index, from, to);
  return path !== null &&
    path.relationships.length === stepCount &&
    path.relationships.every((relationship) => isPrimaryRelationshipProvenance(relationship.status))
    ? path
    : null;
}

function directTemptation(
  index: GraphIndex,
  personId: string,
  candidates: readonly RankedPerson[],
  kind: 'recognizable-direct' | 'unvisited-next-hop',
  used: ReadonlySet<string>,
): NextTemptation | null {
  for (const candidate of candidates) {
    if (used.has(candidate.person.id) || (kind === 'unvisited-next-hop' && candidate.visited)) {
      continue;
    }
    const path = documentedPathWithLength(index, personId, candidate.person.id, 1);
    const relationship = path?.relationships[0];
    if (path === null || relationship === undefined) {
      continue;
    }
    return {
      kind,
      targetId: candidate.person.id,
      targetName: candidate.person.name,
      path,
      reason: {
        kind: 'documented-direct',
        relationshipId: relationship.id,
        relationshipLabel: relationship.label,
        lens: relationship.lens,
      },
    };
  }
  return null;
}

function normalizedDomains(person: GraphPerson): string[] {
  return [
    ...new Set(
      (person.domains ?? [])
        .map((domain) => domain.trim().toLocaleLowerCase('en-US'))
        .filter(Boolean),
    ),
  ].sort((left, right) => left.localeCompare(right));
}

function bridgeReason(
  from: GraphPerson,
  to: GraphPerson,
): Extract<NextTemptationReason, { kind: 'different-domain' | 'birth-year-gap' }> | null {
  const fromDomains = normalizedDomains(from);
  const toDomains = normalizedDomains(to);
  const toDomainSet = new Set(toDomains);
  if (
    fromDomains.length > 0 &&
    toDomains.length > 0 &&
    !fromDomains.some((domain) => toDomainSet.has(domain))
  ) {
    return {
      kind: 'different-domain',
      fromDomain: fromDomains[0]!,
      toDomain: toDomains[0]!,
    };
  }
  if (from.birthYear !== undefined && to.birthYear !== undefined) {
    const yearsApart = Math.abs(from.birthYear - to.birthYear);
    if (yearsApart >= MIN_GENERATIONAL_GAP_YEARS) {
      return {
        kind: 'birth-year-gap',
        fromBirthYear: from.birthYear,
        toBirthYear: to.birthYear,
        yearsApart,
      };
    }
  }
  return null;
}

function twoHopCandidates(options: {
  index: GraphIndex;
  person: GraphPerson;
  visited: ReadonlySet<string>;
  excluded: ReadonlySet<string>;
  used: ReadonlySet<string>;
}): TwoHopCandidate[] {
  const directIds = new Set(options.index.neighborsByPerson.get(options.person.id) ?? []);
  const candidates = new Map<string, TwoHopCandidate>();
  for (const firstConnection of options.index.connectionsByPerson.get(options.person.id) ?? []) {
    if (!isPrimaryRelationshipProvenance(firstConnection.representative.status)) {
      continue;
    }
    const intermediaryId = otherEndpoint(firstConnection.representative, options.person.id);
    const intermediary = options.index.peopleById.get(intermediaryId);
    if (intermediary === undefined) {
      continue;
    }
    for (const secondConnection of options.index.connectionsByPerson.get(intermediaryId) ?? []) {
      if (!isPrimaryRelationshipProvenance(secondConnection.representative.status)) {
        continue;
      }
      const targetId = otherEndpoint(secondConnection.representative, intermediaryId);
      if (
        targetId === options.person.id ||
        directIds.has(targetId) ||
        options.excluded.has(targetId) ||
        options.used.has(targetId)
      ) {
        continue;
      }
      const target = options.index.peopleById.get(targetId);
      if (target === undefined || target.inclusionRole === 'context') {
        continue;
      }
      const reason = bridgeReason(options.person, target);
      if (reason === null) {
        continue;
      }
      const candidate: TwoHopCandidate = {
        person: target,
        degree: options.index.neighborsByPerson.get(target.id)?.length ?? 0,
        visited: options.visited.has(target.id),
        intermediaryId,
        intermediaryRecognition: intermediary.recognitionScore ?? 0,
        reason,
      };
      const prior = candidates.get(targetId);
      if (
        prior === undefined ||
        candidate.intermediaryRecognition > prior.intermediaryRecognition ||
        (candidate.intermediaryRecognition === prior.intermediaryRecognition &&
          candidate.intermediaryId.localeCompare(prior.intermediaryId) < 0)
      ) {
        candidates.set(targetId, candidate);
      }
    }
  }
  return [...candidates.values()].sort(
    (left, right) =>
      compareRankedPeople(left, right) ||
      right.intermediaryRecognition - left.intermediaryRecognition ||
      left.reason.kind.localeCompare(right.reason.kind),
  );
}

function twoHopTemptation(
  index: GraphIndex,
  personId: string,
  candidates: readonly TwoHopCandidate[],
): NextTemptation | null {
  for (const candidate of candidates) {
    const path = documentedPathWithLength(index, personId, candidate.person.id, 2);
    if (path === null) {
      continue;
    }
    return {
      kind: 'two-hop-bridge',
      targetId: candidate.person.id,
      targetName: candidate.person.name,
      path,
      reason: candidate.reason,
    };
  }
  return null;
}

function globalDiscovery(options: {
  index: GraphIndex;
  currentId: string;
  visited: ReadonlySet<string>;
  excluded: ReadonlySet<string>;
  maximum: number;
}): NextTemptation[] {
  return [...options.index.peopleById.values()]
    .filter(
      (person) =>
        person.id !== options.currentId &&
        person.inclusionRole !== 'context' &&
        !options.excluded.has(person.id),
    )
    .map((person) => ({
      person,
      degree: options.index.neighborsByPerson.get(person.id)?.length ?? 0,
      visited: options.visited.has(person.id),
    }))
    .sort(compareRankedPeople)
    .slice(0, options.maximum)
    .map(({ person }) => ({
      kind: 'global-discovery' as const,
      targetId: person.id,
      targetName: person.name,
      path: null,
      reason: { kind: 'global-fallback' as const },
    }));
}

/**
 * Produces at most one result for each connected continuation category. An
 * isolated person receives clearly non-connecting general-discovery results.
 */
export function createNextTemptations(options: NextTemptationOptions): NextTemptation[] {
  const maximum = Math.max(0, Math.min(MAX_TEMPTATIONS, options.maximum ?? MAX_TEMPTATIONS));
  const person = options.index.peopleById.get(options.personId);
  if (maximum === 0 || person === undefined) {
    return [];
  }
  const visited = toSet(options.visitedPersonIds);
  const excluded = toSet(options.excludedPersonIds);
  excluded.add(person.id);
  if ((options.index.neighborsByPerson.get(person.id)?.length ?? 0) === 0) {
    return globalDiscovery({
      index: options.index,
      currentId: person.id,
      visited,
      excluded,
      maximum,
    });
  }

  const directCandidates = eligibleDirectPeople({
    index: options.index,
    personId: person.id,
    visited,
    excluded,
  });
  const temptations: NextTemptation[] = [];
  const used = new Set<string>();
  const recognizable = directTemptation(
    options.index,
    person.id,
    directCandidates,
    'recognizable-direct',
    used,
  );
  if (recognizable !== null) {
    temptations.push(recognizable);
    used.add(recognizable.targetId);
  }
  if (temptations.length < maximum) {
    const bridge = twoHopTemptation(
      options.index,
      person.id,
      twoHopCandidates({ index: options.index, person, visited, excluded, used }),
    );
    if (bridge !== null) {
      temptations.push(bridge);
      used.add(bridge.targetId);
    }
  }
  if (temptations.length < maximum) {
    const unvisited = directTemptation(
      options.index,
      person.id,
      directCandidates,
      'unvisited-next-hop',
      used,
    );
    if (unvisited !== null) {
      temptations.push(unvisited);
    }
  }
  return temptations.slice(0, maximum);
}

export type PersonSummaryNeighbor = {
  id: string;
  name: string;
  recognitionScore: number;
  relationshipId: string;
  relationshipLabel: string;
  lens: RelationshipLens;
};

export type PersonConstellationSummary = {
  personId: string;
  inclusionRole: GraphPerson['inclusionRole'];
  personalCounterpartCount: number;
  familyCounterpartCount: number;
  retainedRecordCount: number;
  componentSize: number;
  recognizableDirectNeighbors: readonly PersonSummaryNeighbor[];
};

export type PersonSummaryModel = {
  get(personId: string): PersonConstellationSummary | null;
  componentSize(personId: string): number;
};

/** Creates one lazy component/summary cache for a specific active-lens index. */
export function createPersonSummaryModel(index: GraphIndex): PersonSummaryModel {
  const componentSizes = new Map<string, number>();
  const summaries = new Map<string, PersonConstellationSummary>();

  function componentSize(personId: string): number {
    const cached = componentSizes.get(personId);
    if (cached !== undefined) {
      return cached;
    }
    if (!index.peopleById.has(personId)) {
      return 0;
    }
    const visited = new Set<string>([personId]);
    const queue = [personId];
    for (let cursor = 0; cursor < queue.length; cursor += 1) {
      for (const neighbor of index.neighborsByPerson.get(queue[cursor]!) ?? []) {
        if (!visited.has(neighbor) && index.peopleById.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }
    for (const member of visited) {
      componentSizes.set(member, visited.size);
    }
    return visited.size;
  }

  function get(personId: string): PersonConstellationSummary | null {
    const cached = summaries.get(personId);
    if (cached !== undefined) {
      return cached;
    }
    const person = index.peopleById.get(personId);
    if (person === undefined) {
      return null;
    }
    const personalCounterparts = new Set<string>();
    const familyCounterparts = new Set<string>();
    let retainedRecordCount = 0;
    for (const connection of index.connectionsByPerson.get(personId) ?? []) {
      const counterpartId = connection.source === personId ? connection.target : connection.source;
      (connection.lens === 'personal' ? personalCounterparts : familyCounterparts).add(
        counterpartId,
      );
      retainedRecordCount += connection.phases.length;
    }
    const recognizableDirectNeighbors = (index.counterpartsByPerson.get(personId) ?? [])
      .map((counterpart) => {
        const neighbor = index.peopleById.get(counterpart.personId);
        const connection = primaryConnection(counterpart.connections);
        return neighbor === undefined || neighbor.inclusionRole === 'context' || connection === null
          ? null
          : { neighbor, connection };
      })
      .filter(
        (
          entry,
        ): entry is {
          neighbor: GraphPerson;
          connection: RelationshipConnection;
        } => entry !== null,
      )
      .sort(
        (left, right) =>
          (right.neighbor.recognitionScore ?? 0) - (left.neighbor.recognitionScore ?? 0) ||
          (index.neighborsByPerson.get(right.neighbor.id)?.length ?? 0) -
            (index.neighborsByPerson.get(left.neighbor.id)?.length ?? 0) ||
          left.neighbor.name.localeCompare(right.neighbor.name) ||
          left.neighbor.id.localeCompare(right.neighbor.id),
      )
      .slice(0, 3)
      .map(({ neighbor, connection }) => ({
        id: neighbor.id,
        name: neighbor.name,
        recognitionScore: neighbor.recognitionScore ?? 0,
        relationshipId: connection.representative.id,
        relationshipLabel: connection.representative.label,
        lens: connection.lens,
      }));
    const summary: PersonConstellationSummary = {
      personId,
      inclusionRole: person.inclusionRole,
      personalCounterpartCount: personalCounterparts.size,
      familyCounterpartCount: familyCounterparts.size,
      retainedRecordCount,
      componentSize: componentSize(personId),
      recognizableDirectNeighbors,
    };
    summaries.set(personId, summary);
    return summary;
  }

  return { get, componentSize };
}

export type AlternativeRouteLabel = 'fewer-steps' | 'different-documented-route';

export type AlternativeDocumentedPath = {
  path: GraphPath;
  label: AlternativeRouteLabel;
  labelText: 'Fewer steps' | 'Different source-linked route';
  stepDifference: number;
  costDifference: number;
};

export type AlternativePathOptions = {
  index: GraphIndex;
  source: string;
  target: string;
  primaryPath?: GraphPath | null;
  maximumAlternatives?: number;
  searchBudget?: number;
};

function pathSignature(path: GraphPath): string {
  return `${path.people.join('>')}::${path.relationships.map(({ id }) => id).join('>')}`;
}

function connectionKeys(index: GraphIndex, path: GraphPath): string[] {
  return path.relationships
    .map((relationship) => index.connectionByRelationshipId.get(relationship.id)?.key)
    .filter((key): key is string => key !== undefined);
}

function indexWithoutConnections(index: GraphIndex, banned: ReadonlySet<string>): GraphIndex {
  return {
    ...index,
    connectionsByPerson: new Map(
      [...index.connectionsByPerson].map(([personId, connections]) => [
        personId,
        connections.filter((connection) => !banned.has(connection.key)),
      ]),
    ),
  };
}

function isLoopFree(path: GraphPath): boolean {
  return new Set(path.people).size === path.people.length;
}

function isMateriallyDistinct(candidate: GraphPath, reference: GraphPath): boolean {
  if (candidate.people.join('>') === reference.people.join('>')) {
    return false;
  }
  const candidateInternal = new Set(candidate.people.slice(1, -1));
  const referenceInternal = new Set(reference.people.slice(1, -1));
  if ([...candidateInternal].some((personId) => !referenceInternal.has(personId))) {
    return true;
  }
  const candidateEdges = new Set(candidate.relationships.map(({ id }) => id));
  const referenceEdges = new Set(reference.relationships.map(({ id }) => id));
  const symmetricDifference =
    [...candidateEdges].filter((id) => !referenceEdges.has(id)).length +
    [...referenceEdges].filter((id) => !candidateEdges.has(id)).length;
  return symmetricDifference >= 2;
}

type AlternativeSearchState = {
  banned: readonly string[];
  depth: number;
};

/**
 * Finds bounded loop-free alternatives by temporarily removing semantic
 * connections and delegating every route calculation to bestDocumentedPath.
 * The original index and primary solver remain unchanged.
 */
export function findAlternativeDocumentedPaths(
  options: AlternativePathOptions,
): AlternativeDocumentedPath[] {
  const maximum = Math.max(0, Math.min(2, options.maximumAlternatives ?? 2));
  const searchBudget = Math.max(
    0,
    Math.min(64, options.searchBudget ?? DEFAULT_ALTERNATIVE_SEARCH_BUDGET),
  );
  if (maximum === 0 || searchBudget === 0 || options.source === options.target) {
    return [];
  }
  const primary =
    options.primaryPath ?? bestDocumentedPath(options.index, options.source, options.target);
  if (
    primary === null ||
    primary.people[0] !== options.source ||
    primary.people.at(-1) !== options.target ||
    primary.relationships.length === 0
  ) {
    return [];
  }
  const primaryConnectionKeys = [...new Set(connectionKeys(options.index, primary))];
  if (primaryConnectionKeys.length === 0) {
    return [];
  }
  const queue: AlternativeSearchState[] = primaryConnectionKeys.map((key) => ({
    banned: [key],
    depth: 1,
  }));
  const queued = new Set(queue.map(({ banned }) => banned.join('|')));
  const candidates = new Map<string, GraphPath>();
  let searches = 0;
  while (queue.length > 0 && searches < searchBudget) {
    queue.sort(
      (left, right) =>
        left.depth - right.depth || left.banned.join('|').localeCompare(right.banned.join('|')),
    );
    const state = queue.shift();
    if (state === undefined) {
      break;
    }
    searches += 1;
    const path = bestDocumentedPath(
      indexWithoutConnections(options.index, new Set(state.banned)),
      options.source,
      options.target,
    );
    if (path === null || !isLoopFree(path)) {
      continue;
    }
    const signature = pathSignature(path);
    if (signature !== pathSignature(primary) && isMateriallyDistinct(path, primary)) {
      candidates.set(signature, path);
    }
    if (state.depth >= MAX_ALTERNATIVE_SEARCH_DEPTH) {
      continue;
    }
    for (const key of connectionKeys(options.index, path)) {
      if (state.banned.includes(key)) {
        continue;
      }
      const banned = [...state.banned, key].sort((left, right) => left.localeCompare(right));
      const stateSignature = banned.join('|');
      if (!queued.has(stateSignature)) {
        queued.add(stateSignature);
        queue.push({ banned, depth: state.depth + 1 });
      }
    }
  }

  const selected: GraphPath[] = [];
  for (const candidate of [...candidates.values()].sort(
    (left, right) =>
      left.totalCost - right.totalCost ||
      left.relationships.length - right.relationships.length ||
      pathSignature(left).localeCompare(pathSignature(right)),
  )) {
    if (selected.every((prior) => isMateriallyDistinct(candidate, prior))) {
      selected.push(candidate);
    }
    if (selected.length >= maximum) {
      break;
    }
  }
  return selected.map((path) => {
    const fewerSteps = path.relationships.length < primary.relationships.length;
    return {
      path,
      label: fewerSteps ? 'fewer-steps' : 'different-documented-route',
      labelText: fewerSteps ? 'Fewer steps' : 'Different source-linked route',
      stepDifference: path.relationships.length - primary.relationships.length,
      costDifference: path.totalCost - primary.totalCost,
    };
  });
}
